import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffectiveTenantId } from './useTenant';

export interface ZClosure {
  id: string;
  closure_number: number;
  closure_date: string;
  period_start: string;
  period_end: string;
  total_ht: number;
  total_tva: number;
  total_ttc: number;
  transaction_count: number;
  first_ticket: string | null;
  last_ticket: string | null;
  tva_details: { tva_rate: number; total_ht: number; total_tva: number; total_ttc: number }[];
  payment_breakdown: Record<string, number>;
  refunds_count: number;
  refunds_total_ttc: number;
  refunds_tva_details: {
    tva_rate: number;
    total_ht: number;
    total_tva: number;
    total_ttc: number;
  }[];
  refunds_payment_breakdown: Record<string, number>;
  /** Règlements d'encours de la période, hors CA. Null sur les Z antérieurs. */
  encours_settlements: unknown;
  cumulative_gt: number;
  hash: string;
  prev_hash: string | null;
  created_at: string;
}

export interface FiscalEvent {
  id: string;
  event_type: string;
  event_date: string;
  user_id: string | null;
  details: Record<string, unknown>;
  hash: string | null;
  created_at: string;
}

export interface GrandTotal {
  id: string;
  cumulative_total: number;
  cumulative_count: number;
  last_ticket_number: string | null;
  last_hash: string | null;
  updated_at: string;
}

export function useZClosures() {
  const effectiveTenantId = useEffectiveTenantId();

  return useQuery({
    queryKey: ['z_closures', effectiveTenantId],
    queryFn: async () => {
      if (!effectiveTenantId) return [];
      const { data, error } = await supabase
        .from('z_closures')
        .select('*')
        .eq('tenant_id', effectiveTenantId)
        .order('closure_number', { ascending: false });
      if (error) throw error;
      return data as unknown as ZClosure[];
    },
    enabled: !!effectiveTenantId,
  });
}

export function useFiscalEvents(limit = 100) {
  const effectiveTenantId = useEffectiveTenantId();

  return useQuery({
    queryKey: ['fiscal_events', effectiveTenantId, limit],
    queryFn: async () => {
      if (!effectiveTenantId) return [];
      const { data, error } = await supabase
        .from('fiscal_events')
        .select('*')
        .eq('tenant_id', effectiveTenantId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data as unknown as FiscalEvent[];
    },
    enabled: !!effectiveTenantId,
  });
}

export function useGrandTotal() {
  const effectiveTenantId = useEffectiveTenantId();

  return useQuery({
    queryKey: ['fiscal_grand_totals', effectiveTenantId],
    queryFn: async () => {
      if (!effectiveTenantId) return null;
      const { data, error } = await supabase
        .from('fiscal_grand_totals')
        .select('*')
        .eq('tenant_id', effectiveTenantId)
        .order('updated_at', { ascending: false })
        .limit(1);
      if (error) throw error;
      return (data as unknown as GrandTotal[])?.[0] || null;
    },
    enabled: !!effectiveTenantId,
  });
}

export async function triggerManualZClosure(userId: string) {
  const { data, error } = await supabase.rpc('perform_z_closure', {
    p_user_id: userId,
  });
  if (error) throw error;
  return data as unknown as Record<string, unknown>;
}
